import type { Status } from "@repo/database";
import type { ProductSchema, UpdateProductSchema } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import type { Product } from "~/providers/product-provider";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useProductStore } from "~/stores/use-product-store";
import { usePermissionManager } from "./use-permission-manager";

export const useProductManager = () => {
  const { t } = useTranslation("common");

  const utils = api.useUtils();

  const { handleProductDelete, handleProductUpdate } = useProductUpdates();

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const { hasPermission } = usePermissionManager();

  const { setProductFormOpen, setCurrentProduct } = useProductStore();

  const { mutate: createProduct } = api.product.createProduct.useMutation({
    async onSuccess(id) {
      toast.success(
        capitalize(t("create_item_success", { item: t("product") })),
      );
      await handleProductUpdate(id);
    },
    onError() {
      toast.error(capitalize(t("create_item_error", { item: t("product") })));
    },
  });

  const { mutate: updateProduct } = api.product.updateProduct.useMutation({
    async onSuccess(_, { id }) {
      toast.success(
        capitalize(t("update_item_success", { item: t("product") })),
      );
      await handleProductUpdate(id);
    },
    onError() {
      toast.error(capitalize(t("update_item_error", { item: t("product") })));
    },
  });

  const { mutate: updateProductStatus } =
    api.product.updateProductStatus.useMutation({
      async onMutate(updatedProduct) {
        await utils.product.getAll.cancel();

        const prevData = utils.product.getAll.getData();

        utils.product.getAll.setData(undefined, (old) => {
          if (!old) return prevData;

          const productToUpdate = old.find(
            (product) => product.id === updatedProduct.id,
          );

          if (!productToUpdate) {
            return [...old];
          }

          return [
            ...old.filter((product) => product.id !== productToUpdate.id),
            {
              ...productToUpdate,
              status: updatedProduct.status,
            },
          ];
        });

        return { prevData };
      },
    });

  const { mutate: deleteProduct } = api.product.deleteProduct.useMutation({
    onSuccess(_, variables) {
      toast.success(
        capitalize(t("delete_item_success", { item: t("product") })),
      );
      handleProductDelete(variables);
    },
    onError() {
      toast.error(capitalize(t("delete_item_fail", { item: t("product") })));
    },
  });

  const createProductHandler = async (
    product: z.infer<ReturnType<typeof ProductSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_product,
      label: t("Permissions.create_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      createProduct(product, {
        onSuccess: () => {
          resolve(true);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const updateProductHandler = async (
    product: z.infer<ReturnType<typeof UpdateProductSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product,
      label: t("Permissions.update_product_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      updateProduct(
        {
          ...product,
          notifyPrintRouteId: product.notifyPrintRouteId ?? undefined,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(false);
          },
        },
      );
    });
  };

  const updateProductStatusHandler = async (id: string, status: Status) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_product,
      label: t("Permissions.update_product_label"),
    });

    if (!permission) return false;
    return await new Promise<boolean>((resolve) => {
      updateProductStatus(
        {
          id,
          status,
        },
        {
          onSuccess: () => {
            toast.success(t("update_item_success", { item: t("product") }));
            resolve(true);
          },
          onError: () => {
            toast.error(t("update_item_error", { item: t("product") }));
            resolve(false);
          },
        },
      );
    });
  };

  const deleteProductHandler = async (id: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_product,
      label: t("Permissions.delete_product_label"),
    });

    if (!permission) return false;

    const result = await showDialog({
      title: t("before_delete_product_title"),
      message: t("before_delete_product_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return false;

    return new Promise<boolean>((resolve) => {
      deleteProduct(id, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const openProductDialog = (product?: Product) => {
    setProductFormOpen(true);
    setCurrentProduct(product);
  };
  return {
    openProductDialog,
    createProduct: createProductHandler,
    updateProduct: updateProductHandler,
    updateProductStatus: updateProductStatusHandler,
    deleteProduct: deleteProductHandler,
  };
};

export const useProductUpdates = () => {
  const utils = api.useUtils();

  const handleProductUpdate = useCallback(
    async (productId: string) => {
      const result = await utils.product.getById.fetch(productId);
      // TODO: fix this
      await utils.productModifierGroup.getAll.invalidate();
      void utils.product.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [
      utils.product.getAll,
      utils.product.getById,
      utils.productModifierGroup.getAll,
    ],
  );

  const handleProductDelete = useCallback(
    (productId: string) => {
      void utils.product.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== productId);
      });
    },
    [utils.product.getAll],
  );

  return {
    handleProductUpdate,
    handleProductDelete,
  };
};
